import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../contexts/authContext';
import { moveFoodItemBetweenContainers } from '../data/AsyncFunctions/moveFoodItemBetweenContainers';
import FoodItem from './FoodItem';

function ContainerWrapper({ items, bgColor, name, containerKey }) {
  const dispatch = useDispatch();
  const { userLoggedIn } = useAuth();
  const [isDropTarget, setIsDropTarget] = useState(false);
  const [moveError, setMoveError] = useState('');

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setIsDropTarget(true);
  };

  const handleDragLeave = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    setIsDropTarget(false);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDropTarget(false);
    setMoveError('');

    try {
      const rawPayload = event.dataTransfer.getData('application/json');

      if (!rawPayload) {
        return;
      }

      const draggedItem = JSON.parse(rawPayload);

      if (
        !draggedItem?.id ||
        !draggedItem?.sourceContainerKey ||
        draggedItem.sourceContainerKey === containerKey
      ) {
        return;
      }

      const moveResult = await moveFoodItemBetweenContainers(
        userLoggedIn,
        draggedItem.sourceContainerKey,
        containerKey,
        draggedItem
      );

      dispatch({
        type: 'MOVE_ITEM',
        payload: {
          sourceContainerKey: draggedItem.sourceContainerKey,
          destinationContainerKey: containerKey,
          item: moveResult?.movedItem ?? {
            id: draggedItem.id,
            name: draggedItem.name,
            quantity: draggedItem.quantity,
            expirationDate: draggedItem.expirationDate,
          },
        },
      });
    } catch (error) {
      setMoveError('Unable to move item right now.');
    }
  };

  return (
    <div
      className={`relative ${bgColor} h-full overflow-y-auto rounded-lg p-3 shadow-lg transition-all duration-150 ${
        isDropTarget ? 'ring-2 ring-emerald-500 ring-offset-2' : ''
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <span className="absolute right-2 top-2 text-xs font-semibold text-gray-500 px-1 rounded">
        {name}
      </span>

      <ul className="flex flex-wrap gap-3 pt-6">
        {items.map((item, index) => (
          <FoodItem
            key={item.id ?? `${item.name}-${index}`}
            id={item.id}
            containerKey={containerKey}
            name={item.name}
            quantity={item.quantity}
            expirationDate={item.expirationDate}
          />
        ))}
      </ul>
      {moveError && <p className="pt-2 text-xs text-red-700">{moveError}</p>}
    </div>
  );
}

function Freezer() {
  const freezerContents = useSelector((state) => state.freezer_contents);

  return (
    <ContainerWrapper
      items={freezerContents}
      bgColor="bg-blue-200"
      name="Freezer"
      containerKey="freezer_contents"
    />
  );
}

function FridgeLevel() {
  const fridgeLevelContents = useSelector((state) => state.fridge_level_contents);

  return (
    <ContainerWrapper
      items={fridgeLevelContents}
      bgColor="bg-blue-100"
      name="Fridge Level"
      containerKey="fridge_level_contents"
    />
  );
}

function FridgeDoor() {
  const fridgeDoorContents = useSelector((state) => state.fridge_door_contents);

  return (
    <ContainerWrapper
      items={fridgeDoorContents}
      bgColor="bg-blue-100"
      name="Fridge Door"
      containerKey="fridge_door_contents"
    />
  );
}

function Pantry() {
  const pantryContents = useSelector((state) => state.pantry_contents);

  return (
    <ContainerWrapper
      items={pantryContents}
      bgColor="bg-orange-100"
      name="Pantry"
      containerKey="pantry_contents"
    />
  );
}

function SpiceRack() {
  const spiceRackContents = useSelector((state) => state.spice_rack_contents);

  return (
    <ContainerWrapper
      items={spiceRackContents}
      bgColor="bg-orange-200"
      name="Spice Rack"
      containerKey="spice_rack_contents"
    />
  );
}

function Other() {
  const otherContents = useSelector((state) => state.other_contents);

  return (
    <ContainerWrapper
      items={otherContents}
      bgColor="bg-amber-100"
      name="Other"
      containerKey="other_contents"
    />
  );
}

function FreezerDoor() {
  const freezerDoorContents = useSelector((state) => state.freezer_door_contents);

  return (
    <ContainerWrapper
      items={freezerDoorContents}
      bgColor="bg-blue-200"
      name="Freezer Door"
      containerKey="freezer_door_contents"
    />
  );
}

function CrisperLeft() {
  const crisperLeftContents = useSelector((state) => state.crisper_left_contents);

  return (
    <ContainerWrapper
      items={crisperLeftContents}
      bgColor="bg-blue-100"
      name="Crisper (Left)"
      containerKey="crisper_left_contents"
    />
  );
}

function CrisperRight() {
  const crisperRightContents = useSelector((state) => state.crisper_right_contents);

  return (
    <ContainerWrapper
      items={crisperRightContents}
      bgColor="bg-blue-100"
      name="Crisper (Right)"
      containerKey="crisper_right_contents"
    />
  );
}

export {
  Freezer,
  FridgeLevel,
  FridgeDoor,
  Pantry,
  SpiceRack,
  FreezerDoor,
  CrisperLeft,
  CrisperRight,
  Other,
};
